import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TranscriptionSettings as Settings } from "../lib/api";
import { Icon } from "./Icon";

const WHISPER_LANGUAGE_CODES = [
  "af", "am", "ar", "as", "az", "ba", "be", "bg", "bn", "bo", "br", "bs", "ca", "cs", "cy", "da", "de", "el", "en", "es",
  "et", "eu", "fa", "fi", "fo", "fr", "gl", "gu", "ha", "haw", "he", "hi", "hr", "ht", "hu", "hy", "id", "is", "it", "ja",
  "jw", "ka", "kk", "km", "kn", "ko", "la", "lb", "ln", "lo", "lt", "lv", "mg", "mi", "mk", "ml", "mn", "mr", "ms", "mt",
  "my", "ne", "nl", "nn", "no", "oc", "pa", "pl", "ps", "pt", "ro", "ru", "sa", "sd", "si", "sk", "sl", "sn", "so", "sq",
  "sr", "su", "sv", "sw", "ta", "te", "tg", "th", "tk", "tl", "tr", "tt", "uk", "ur", "uz", "vi", "yi", "yo", "zh",
] as const;

export const DEFAULT_TRANSCRIPTION_SETTINGS: Settings = {
  language: null,
  mode: "balanced",
  diarizationEnabled: true,
  speakerCountHint: null,
  audioStreamIndex: null,
  audioRepairMode: "off",
  advanced: {
    task: "transcribe",
    response_format: "verbose_json",
    temperature: 0,
    timestamp_granularities: "segment,word",
    initial_prompt: "",
    prefix: "",
    beam_size: 5,
    condition_on_previous_text: true,
    vad_filter: true,
    no_speech_threshold: 0.6,
    compression_ratio_threshold: 2.4,
    log_prob_threshold: -1,
    hallucination_silence_threshold: null,
  },
};

export interface TranscriptionFeatureAvailability {
  speakerDiarization: boolean;
  audioEnhancement: boolean;
}

interface Props {
  open: boolean;
  settings: Settings;
  onChange: (settings: Settings) => void;
  onClose: () => void;
  onStart: () => void;
  busy: boolean;
  quotaSummary?: string;
  startLabel?: string;
  availability?: TranscriptionFeatureAvailability;
}

export function TranscriptionSettings({ open, settings, onChange, onClose, onStart, busy, quotaSummary, startLabel, availability }: Props) {
  const { t, i18n } = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  if (!open) return null;

  const languageNames = new Intl.DisplayNames([i18n.resolvedLanguage ?? i18n.language], { type: "language" });

  const setAdvanced = (key: string, value: string | number | boolean | null) => {
    onChange({ ...settings, advanced: { ...settings.advanced, [key]: value } });
  };

  return (
    <section id="transcription-settings-popover" className="transcription-popover" role="dialog" aria-modal="false" aria-label={t("settings.title")}>
      <header className="transcription-popover__head">
        <div><p className="eyebrow">{t("settings.eyebrow")}</p><h3>{t("settings.title")}</h3><p>{t("settings.description")}</p></div>
        <button type="button" className="icon-button popover-close" aria-label={t("settings.close")} onClick={onClose}><Icon name="close" /></button>
      </header>
      <div className="common-settings-grid">
        <Setting label={t("settings.sourceLanguage")}>
          <select value={settings.language ?? "auto"} onChange={(event) => onChange({ ...settings, language: event.target.value === "auto" ? null : event.target.value })}>
            <option value="auto">{t("source.auto")}</option>
            {WHISPER_LANGUAGE_CODES.map((code) => <option key={code} value={code}>{languageNames.of(code) ?? code}</option>)}
          </select>
        </Setting>
        <Setting label={t("settings.mode")}>
          <select value={settings.mode} onChange={(event) => onChange({ ...settings, mode: event.target.value as Settings["mode"] })}>
            <option value="fast">{t("settings.fast")}</option><option value="balanced">{t("settings.balanced")}</option><option value="accurate">{t("settings.accurate")}</option>
          </select>
        </Setting>
        <Setting label={t("settings.speaker")}>
          <select disabled={availability?.speakerDiarization === false} value={!settings.diarizationEnabled || availability?.speakerDiarization === false ? "off" : settings.speakerCountHint ?? "auto"} onChange={(event) => {
            const value = event.target.value;
            onChange({ ...settings, diarizationEnabled: value !== "off", speakerCountHint: value === "auto" || value === "off" ? null : Number(value) });
          }}>
            <option value="off">{t("settings.off")}</option><option value="auto">{t("settings.automatic")}</option><option value="2">{t("settings.two")}</option><option value="3">{t("settings.three")}</option><option value="4">{t("settings.many")}</option>
          </select>
        </Setting>
        <Setting label={t("settings.track")}>
          <select value={settings.audioStreamIndex ?? "auto"} onChange={(event) => onChange({ ...settings, audioStreamIndex: event.target.value === "auto" ? null : Number(event.target.value) })}>
            <option value="auto">{t("settings.mainTrack")}</option>
            {settings.audioStreamIndex !== null && <option value={settings.audioStreamIndex}>{t("settings.confirmTrack")} · {settings.audioStreamIndex + 1}</option>}
          </select>
        </Setting>
        <Setting wide label={t("settings.audioRepair")}>
          <select disabled={availability?.audioEnhancement === false} value={availability?.audioEnhancement === false ? "off" : settings.audioRepairMode} onChange={(event) => onChange({ ...settings, audioRepairMode: event.target.value as Settings["audioRepairMode"] })}>
            <option value="off">{t("settings.repairOff")}</option><option value="denoise">{t("settings.denoise")}</option><option value="enhance">{t("settings.enhanceSpeech")}</option>
          </select>
        </Setting>
      </div>
      <button type="button" className={`advanced-settings-toggle${advancedOpen ? " is-open" : ""}`} aria-expanded={advancedOpen} onClick={() => setAdvancedOpen((value) => !value)}>
        <span><strong>{t("settings.advanced")}</strong><small>{t("settings.advancedHint")}</small></span><Icon name="chevron" />
      </button>
      {advancedOpen && <div className="advanced-settings">
        <div className="advanced-note"><b>Whisper</b><p>{t("settings.mappingNote")}</p></div>
        <AdvancedSection title={t("settings.taskOutput")}>
          <Setting wide label={t("settings.model")}><input value="@cf/openai/whisper-large-v3-turbo" readOnly /></Setting>
          <Setting label={`${t("settings.task")} · task`}><select value={String(settings.advanced.task)} onChange={(event) => setAdvanced("task", event.target.value)}><option value="transcribe">{t("settings.transcribe")}</option><option value="translate">{t("settings.translateEnglish")}</option></select></Setting>
          <Setting label={`${t("settings.responseFormat")} · response_format`}><input value="verbose_json" readOnly /></Setting>
          <Setting label={`${t("settings.temperature")} · temperature`}><input type="number" min="0" max="1" step="0.1" value={Number(settings.advanced.temperature)} onChange={(event) => setAdvanced("temperature", Number(event.target.value))} /></Setting>
          <Setting label={`${t("settings.timestamps")} · timestamp_granularities`}><select value={String(settings.advanced.timestamp_granularities)} onChange={(event) => setAdvanced("timestamp_granularities", event.target.value)}><option value="segment">{t("settings.segments")}</option><option value="word">{t("settings.words")}</option><option value="segment,word">{t("settings.segmentsWords")}</option></select></Setting>
        </AdvancedSection>
        <AdvancedSection title={t("settings.context")}>
          <Setting wide label={`${t("settings.initialPrompt")} · initial_prompt`}><textarea rows={3} value={String(settings.advanced.initial_prompt)} placeholder={t("settings.promptPlaceholder")} onChange={(event) => setAdvanced("initial_prompt", event.target.value)} /></Setting>
          <Setting wide label={`${t("settings.prefix")} · prefix`}><input value={String(settings.advanced.prefix)} onChange={(event) => setAdvanced("prefix", event.target.value)} /></Setting>
          <Setting label={`${t("settings.beam")} · beam_size`}><input type="number" min="1" max="20" value={Number(settings.advanced.beam_size)} onChange={(event) => setAdvanced("beam_size", Number(event.target.value))} /></Setting>
          <Setting label={`${t("settings.previous")} · condition_on_previous_text`}><select value={String(settings.advanced.condition_on_previous_text)} onChange={(event) => setAdvanced("condition_on_previous_text", event.target.value === "true")}><option value="true">{t("settings.enabled")}</option><option value="false">{t("settings.disabled")}</option></select></Setting>
        </AdvancedSection>
        <AdvancedSection title={t("settings.silence")}>
          <label className="switch-field wide"><span><strong>{t("settings.vad")}</strong><small>vad_filter · {t("settings.vadHint")}</small></span><input type="checkbox" checked={Boolean(settings.advanced.vad_filter)} onChange={(event) => setAdvanced("vad_filter", event.target.checked)} /><i /></label>
          <Setting label={`${t("settings.noSpeech")} · no_speech_threshold`}><input type="number" step="0.1" value={Number(settings.advanced.no_speech_threshold)} onChange={(event) => setAdvanced("no_speech_threshold", Number(event.target.value))} /></Setting>
          <Setting label={`${t("settings.compression")} · compression_ratio_threshold`}><input type="number" step="0.1" value={Number(settings.advanced.compression_ratio_threshold)} onChange={(event) => setAdvanced("compression_ratio_threshold", Number(event.target.value))} /></Setting>
          <Setting label={`${t("settings.logProb")} · log_prob_threshold`}><input type="number" step="0.1" value={Number(settings.advanced.log_prob_threshold)} onChange={(event) => setAdvanced("log_prob_threshold", Number(event.target.value))} /></Setting>
          <Setting label={`${t("settings.hallucination")} · hallucination_silence_threshold`}><input type="number" step="0.1" value={settings.advanced.hallucination_silence_threshold == null ? "" : Number(settings.advanced.hallucination_silence_threshold)} onChange={(event) => setAdvanced("hallucination_silence_threshold", event.target.value === "" ? null : Number(event.target.value))} /></Setting>
        </AdvancedSection>
      </div>}
      <div className="popover-quota"><Icon name="clock" /><p><strong>{t("settings.billing")}</strong><small>{quotaSummary ?? t("settings.remaining")}</small></p></div>
      <footer className="transcription-popover__actions"><button type="button" className="text-button" onClick={() => onChange(applyTranscriptionFeatureAvailability(structuredClone(DEFAULT_TRANSCRIPTION_SETTINGS), availability))}>{t("settings.reset")}</button><button type="button" className="button button--primary" disabled={busy} onClick={onStart}>{busy ? t("common.loading") : startLabel ?? t("settings.startWith")}</button></footer>
    </section>
  );
}

export function applyTranscriptionFeatureAvailability(
  settings: Settings,
  availability?: TranscriptionFeatureAvailability,
): Settings {
  if (!availability) return settings;
  return {
    ...settings,
    diarizationEnabled: availability.speakerDiarization && settings.diarizationEnabled,
    speakerCountHint: availability.speakerDiarization ? settings.speakerCountHint : null,
    audioRepairMode: availability.audioEnhancement ? settings.audioRepairMode : "off",
  };
}

function Setting({ label, wide = false, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return <label className={`setting-field${wide ? " wide" : ""}`}><span>{label}</span>{children}</label>;
}

function AdvancedSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="advanced-section"><h4>{title}</h4><div className="advanced-grid">{children}</div></section>;
}
